import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { supabase } from "../lib/supabase"
import { DateFilter } from "../types/filters"

export interface OrientationAttendanceData {
  totalSessions: number
  totalBookings: number
  attendedCount: number
  noShowCount: number
  attendanceRate: number
}

/**
 * Hook to fetch orientation attendance data from the metrics-orientation-attendance endpoint
 * @param dateFilter Optional date range filter
 * @returns Query result with orientation attendance data
 */
export const useOrientationAttendanceData = (dateFilter?: DateFilter, jobId?: string) => {
  const query = useQuery<OrientationAttendanceData>({
    queryKey: ["orientationAttendance", dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-orientation-attendance", {
        body: {
          startDate: dateFilter?.startDate || null,
          endDate: dateFilter?.endDate || null,
          jobId: jobId ?? null,
        },
      })

      if (error) {
        throw new Error(`Error fetching orientation attendance data: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
